import { Project } from '../models/project.model';

/** Abstract GP summary row created from Excel "GP Name + count + total cost" imports. */
export function isFeAbstractGpSummaryProject(p: Project): boolean {
    const name = (p.name || '').trim();
    if (/ - non-started projects$/i.test(name)) return true;
    const remarks = (p.remarks || '').toLowerCase();
    return remarks.includes('abstract import') || remarks.includes('non-started project(s)');
}

/**
 * Projects used for GP/block cost & count rollups.
 * When detailed project rows exist, drop the abstract GP summary placeholder
 * so Est. Cost totals match the sum of listed projects.
 */
export function rollupProjectsForGpTotals(projects: Project[]): Project[] {
    const detailed = projects.filter(p => !isFeAbstractGpSummaryProject(p));
    return detailed.length > 0 ? detailed : [...projects];
}

/** Standard name for abstract GP placeholder rows (import + cleanup). */
export function feAbstractGpProjectName(gpName: string): string {
    return `${gpName} - Non-Started Projects`;
}

/** Physical progress % — prefers `physical`, falls back to `progress`. */
export function getProjectPhysical(p: Project): number {
    return p.physical ?? p.progress ?? 0;
}

/** On Track: completed or physical progress ≥ 70%. */
export function isFeOnTrack(p: Project): boolean {
    return p.status === 'Completed' || getProjectPhysical(p) >= 70;
}

/** In Progress: explicit status or physical progress 30%–69% (excludes completed / stuck). */
export function isFeInProgress(p: Project): boolean {
    if (p.status === 'Completed' || p.status === 'Stuck') return false;
    if (p.status === 'In Progress') return true;
    const phys = getProjectPhysical(p);
    return phys >= 30 && phys < 70;
}

/** Delayed: stuck, or > 60 days past target (not completed). */
export function isFeDelayed(p: Project, today = new Date()): boolean {
    if (p.status === 'Completed') return false;
    if (p.status === 'Stuck') return true;
    const target = new Date(p.targetDate || p.end);
    if (isNaN(target.getTime())) return false;
    return Math.floor((today.getTime() - target.getTime()) / 86400000) > 60;
}

export type FeProgressBucket = 'onTrack' | 'inProgress' | 'delayed' | 'other';

/** Mutually exclusive bucket for block / GP summary cards. */
export function classifyFeProgressBucket(p: Project, today = new Date()): FeProgressBucket {
    if (isFeOnTrack(p)) return 'onTrack';
    if (isFeInProgress(p)) return 'inProgress';
    if (isFeDelayed(p, today)) return 'delayed';
    return 'other';
}

export interface FeAggregatedStats {
    totalProjects: number;
    /** Sum of project costs in ₹ Lakh. */
    sanctionedAmount: number;
    onTrackCount: number;
    onTrackPercent: number;
    inProgressCount: number;
    inProgressPercent: number;
    delayedCount: number;
    delayedPercent: number;
    avgProgress: number;
}

/** Aggregate block / GP table stats from live project rows. */
export function aggregateFeStats(projects: Project[]): FeAggregatedStats {
    const rows = rollupProjectsForGpTotals(projects);
    const total = rows.length;
    let onTrack = 0;
    let inProgress = 0;
    let delayed = 0;
    let costSum = 0;
    let progressSum = 0;
    const today = new Date();

    for (const p of rows) {
        costSum += p.cost || 0;
        progressSum += getProjectPhysical(p);
        switch (classifyFeProgressBucket(p, today)) {
            case 'onTrack':    onTrack++;    break;
            case 'inProgress': inProgress++; break;
            case 'delayed':    delayed++;    break;
        }
    }

    const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

    return {
        totalProjects: total,
        sanctionedAmount: costSum,
        onTrackCount: onTrack,
        onTrackPercent: pct(onTrack),
        inProgressCount: inProgress,
        inProgressPercent: pct(inProgress),
        delayedCount: delayed,
        delayedPercent: pct(delayed),
        avgProgress: total > 0 ? Math.round(progressSum / total) : 0,
    };
}

export interface GpStatusStats {
    total: number;
    onTrack: number;
    inProgress: number;
    delayed: number;
    stuckOrNS: number;
    totalCost: number;
}

/** GP project-view summary — exact status counts from database. */
export function aggregateGpStatusStats(projects: Project[]): GpStatusStats {
    const rows = rollupProjectsForGpTotals(projects);
    return {
        total: rows.length,
        onTrack: rows.filter(p => p.status === 'Completed').length,
        inProgress: rows.filter(p => p.status === 'In Progress').length,
        delayed: rows.filter(p => p.status === 'Stuck').length,
        stuckOrNS: rows.filter(p => p.status === 'Planned').length,
        totalCost: rows.reduce((s, p) => s + (p.cost || 0), 0),
    };
}

/** Format a cost stored in ₹ Lakh for display. */
export function formatCostLakh(amount: number | undefined | null, decimals = 2): string {
    const v = amount ?? 0;
    return '₹' + v.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }) + ' L';
}
